import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Inspection, Station, Bay, Segment, Support, IssueType, ElementType, NotInspectedElement } from '../types';

interface InspectionStore {
  // Data
  inspection: Inspection | null;
  isLoaded: boolean;
  error: string | null;
  
  // Not inspected elements (for SWIM export)
  notInspectedElements: NotInspectedElement[];

  // Actions
  setInspection: (inspection: Inspection) => void;
  clearInspection: () => void;
  setError: (error: string | null) => void;
  
  // Not inspected elements actions
  markAsNotInspected: (elementId: number, elementType: ElementType) => void;
  markAsInspected: (elementId: number) => void;
  clearNotInspected: () => void;
  isElementNotInspected: (elementId: number) => boolean;

  // Selectors
  getStations: () => Station[];
  getBays: () => Bay[];
  getSegments: () => Segment[];
  getSupports: () => Support[];
  getIssueTypes: (elementType?: ElementType) => IssueType[];
  getElementById: (id: number) => Station | Bay | Segment | Support | null;
  getElementType: (id: number) => ElementType | null;
  getNotInspectedElements: () => NotInspectedElement[];
}

// Helper to convert ElementType to NotInspectedElement type
function toNotInspectedType(elementType: ElementType): NotInspectedElement['elementType'] {
  switch (elementType) {
    case 'SEGMENT': return 'segment';
    case 'BAY': return 'bay';
    case 'STATION': return 'station';
    case 'SUPPORT': return 'segment'; // Supports are mapped to segments in SWIM
    default: return 'segment';
  }
}

export const useInspectionStore = create<InspectionStore>()(
  persist(
    (set, get) => ({
      inspection: null,
      isLoaded: false,
      error: null,
      notInspectedElements: [],

      setInspection: (inspection) => set({ inspection, isLoaded: true, error: null }),
      
      clearInspection: () => set({ inspection: null, isLoaded: false, error: null, notInspectedElements: [] }),
      
      setError: (error) => set({ error }),

      // Not inspected elements management
      markAsNotInspected: (elementId: number, elementType: ElementType) => {
        const current = get().notInspectedElements;
        const elementIdStr = String(elementId);
        
        // Check if already marked
        if (current.some(e => e.elementId === elementIdStr)) {
          return;
        }
        
        set({
          notInspectedElements: [
            ...current,
            {
              elementType: toNotInspectedType(elementType),
              elementId: elementIdStr,
            }
          ]
        });
      },

      markAsInspected: (elementId: number) => {
        const elementIdStr = String(elementId);
        set({
          notInspectedElements: get().notInspectedElements.filter(
            e => e.elementId !== elementIdStr
          )
        });
      },

      clearNotInspected: () => set({ notInspectedElements: [] }),

      isElementNotInspected: (elementId: number) => {
        return get().notInspectedElements.some(e => e.elementId === String(elementId));
      },

      getNotInspectedElements: () => get().notInspectedElements,

      getStations: () => get().inspection?.stations ?? [],
      
      getBays: () => get().inspection?.bays ?? [],
      
      getSegments: () => get().inspection?.segments ?? [],
      
      getSupports: () => get().inspection?.supports ?? [],
      
      getIssueTypes: (elementType?: ElementType) => {
        const issueTypes = get().inspection?.issue_types ?? [];
        if (!elementType) return issueTypes;
        return issueTypes.filter(it => it.elementType.includes(elementType));
      },

      getElementById: (id: number) => {
        const { inspection } = get();
        if (!inspection) return null;

        // Search in stations
        const station = inspection.stations.find(s => s.elementId === id);
        if (station) return station;

        // Search in bays
        const bay = inspection.bays.find(b => b.elementId === id);
        if (bay) return bay;

        // Search in segments
        const segment = inspection.segments.find(s => s.elementId === id);
        if (segment) return segment;

        // Search in supports
        const support = inspection.supports.find(s => s.elementId === id);
        if (support) return support;

        return null;
      },

      getElementType: (id: number) => {
        const { inspection } = get();
        if (!inspection) return null;

        if (inspection.stations.find(s => s.elementId === id)) return 'STATION';
        if (inspection.bays.find(b => b.elementId === id)) return 'BAY';
        if (inspection.segments.find(s => s.elementId === id)) return 'SEGMENT';
        if (inspection.supports.find(s => s.elementId === id)) return 'SUPPORT';

        return null;
      },
    }),
    {
      name: 'elifly-inspection',
      // Persist inspection data and not-inspected elements
      partialize: (state) => ({
        inspection: state.inspection,
        isLoaded: state.isLoaded,
        notInspectedElements: state.notInspectedElements,
      }),
    }
  )
);
